import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import SoftInput from "components/SoftInput";

import { Box, Card, CircularProgress, Collapse, IconButton } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { API_URL } from "config";

const emptyScopeState = {
  branch: { all: false, ids: [] },
  sub_branch: { all: false, ids: [] },
  shift: { all: false, ids: [] },
};

const memberTypeForActor = (type) => {
  if (type === "client") return "client_contact";
  if (type === "vendor") return "vendor_contact";
  return "user";
};

const normalizedMemberIds = (memberIds) => memberIds.map(String).sort().join("|");

const authHeaders = (token, extra = {}) => ({
  Accept: "application/json",
  "X-Requested-With": "XMLHttpRequest",
  Authorization: `Bearer ${token}`,
  ...extra,
});

const RolePermissions = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState(null);
  const [roleName, setRoleName] = useState("");
  const [actorType, setActorType] = useState("internal");
  const [parentRoleId, setParentRoleId] = useState("");
  const [showInProjectManagementDropdown, setShowInProjectManagementDropdown] = useState(false);
  const [parentRoles, setParentRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [scopeOptions, setScopeOptions] = useState({});
  const [selected, setSelected] = useState([]);
  const [scopeState, setScopeState] = useState(emptyScopeState);
  const [userScope, setUserScope] = useState("self");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [initialSelectedMembers, setInitialSelectedMembers] = useState([]);
  const [initialMemberType, setInitialMemberType] = useState("user");
  const [expanded, setExpanded] = useState([]);
  const [snack, setSnack] = useState({ open: false, color: "success", message: "" });

  const storedPermissions = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("permissions") || "[]");
    } catch {
      return [];
    }
  }, []);

  const actorScopes = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("accessScopes") || "{}");
    } catch {
      return {};
    }
  }, []);

  const canAssignPermissions = storedPermissions.includes("access.roles.assign_permissions");
  const canAssignScopes = storedPermissions.includes("access.roles.assign_scopes");
  const canAssignMembers = storedPermissions.includes("access.roles.assign_members");
  const canUpdateRole = storedPermissions.includes("access.roles.update");
  const canViewRoles = storedPermissions.includes("access.roles.view");
  const canManageProjectManagementDropdownFlag =
    storedPermissions.includes("access.system.full_access");

  const grouped = useMemo(() => {
    const g = {};
    permissions.forEach((p) => {
      const parent = p.parent_id ?? 0;
      if (!g[parent]) g[parent] = [];
      g[parent].push(p);
    });
    return g;
  }, [permissions]);

  const hasWildcard = (scopeType) =>
    (actorScopes["*"] || []).includes("*") || (actorScopes[scopeType] || []).includes("*");

  const scopeList = (type) => scopeOptions[type] || [];
  const selectedBranchIds = scopeState.branch.ids.map(String);
  const selectedSubBranchIds = scopeState.sub_branch.ids.map(String);

  const filteredSubBranches = useMemo(() => {
    if (scopeState.branch.all || selectedBranchIds.length === 0) return scopeList("sub_branch");
    return scopeList("sub_branch").filter((row) => selectedBranchIds.includes(String(row.branch_id)));
  }, [scopeOptions, scopeState.branch.all, selectedBranchIds.join(",")]);

  const filteredShifts = useMemo(() => {
    const shifts = scopeList("shift");
    if (!scopeState.sub_branch.all && selectedSubBranchIds.length > 0) {
      return shifts.filter((row) => selectedSubBranchIds.includes(String(row.subbranch_id)));
    }
    if (!scopeState.branch.all && selectedBranchIds.length > 0) {
      return shifts.filter((row) => selectedBranchIds.includes(String(row.branch_id)));
    }
    return shifts;
  }, [
    scopeOptions,
    scopeState.branch.all,
    scopeState.sub_branch.all,
    selectedBranchIds.join(","),
    selectedSubBranchIds.join(","),
  ]);

  const filteredUsers = useMemo(() => {
    const users = scopeOptions.users || [];

    return users.filter((user) => {
      if (!scopeState.branch.all && selectedBranchIds.length > 0 && !selectedBranchIds.includes(String(user.branch_id))) {
        return false;
      }
      if (
        !scopeState.sub_branch.all &&
        selectedSubBranchIds.length > 0 &&
        !selectedSubBranchIds.includes(String(user.subbranch_id))
      ) {
        return false;
      }
      if (!scopeState.shift.all && scopeState.shift.ids.length > 0) {
        const shiftSet = new Set(scopeState.shift.ids.map(String));
        return (user.shift_ids || []).some((shiftId) => shiftSet.has(String(shiftId)));
      }
      return true;
    });
  }, [
    scopeOptions,
    scopeState.branch.all,
    scopeState.sub_branch.all,
    scopeState.shift.all,
    scopeState.shift.ids.join(","),
    selectedBranchIds.join(","),
    selectedSubBranchIds.join(","),
  ]);

  const currentMemberType = memberTypeForActor(actorType);
  const memberOptions = useMemo(() => {
    if (actorType === "client") return scopeOptions.client_contacts || [];
    if (actorType === "vendor") return scopeOptions.vendor_contacts || [];
    return filteredUsers;
  }, [actorType, filteredUsers, scopeOptions.client_contacts, scopeOptions.vendor_contacts]);

  useEffect(() => {
    const allowedMemberIds = new Set(memberOptions.map((option) => String(option.id)));
    setSelectedMembers((prev) => {
      const next = prev.filter((memberId) => allowedMemberIds.has(String(memberId)));
      return next.length === prev.length ? prev : next;
    });
  }, [memberOptions]);

  const memberTitle = actorType === "client"
    ? "Client Contacts"
    : actorType === "vendor"
      ? "Vendor Contacts"
      : "Users";

  const parentRoleOptions = useMemo(
    () =>
      parentRoles.filter(
        (option) =>
          String(option.id) !== String(id) &&
          Boolean(option.id)
      ),
    [actorType, id, parentRoles]
  );

  useEffect(() => {
    if (parentRoleId && !parentRoleOptions.some((option) => String(option.id) === String(parentRoleId))) {
      setParentRoleId("");
    }
  }, [parentRoleId, parentRoleOptions]);

  useEffect(() => {
    if (
      (!canManageProjectManagementDropdownFlag || actorType !== "internal") &&
      showInProjectManagementDropdown
    ) {
      setShowInProjectManagementDropdown(false);
    }
  }, [actorType, canManageProjectManagementDropdownFlag, showInProjectManagementDropdown]);

  const descendantIds = (permissionId) => {
    const children = grouped[permissionId] || [];
    return children.reduce((ids, child) => [...ids, child.id, ...descendantIds(child.id)], []);
  };

  const toggleExpand = (permissionId) => {
    setExpanded((prev) =>
      prev.includes(permissionId) ? prev.filter((item) => item !== permissionId) : [...prev, permissionId]
    );
  };

  const togglePermission = (permissionId) => {
    const ids = [permissionId, ...descendantIds(permissionId)];

    setSelected((prev) => {
      const selectedSet = new Set(prev);
      const shouldRemove = ids.every((idValue) => selectedSet.has(idValue));

      ids.forEach((idValue) => {
        if (shouldRemove) selectedSet.delete(idValue);
        else selectedSet.add(idValue);
      });

      return Array.from(selectedSet);
    });
  };

  const toggleScopeId = (scopeType, scopeId) => {
    const idValue = String(scopeId);
    setScopeState((prev) => {
      const ids = prev[scopeType].ids.includes(idValue)
        ? prev[scopeType].ids.filter((item) => item !== idValue)
        : [...prev[scopeType].ids, idValue];

      return {
        ...prev,
        [scopeType]: { ...prev[scopeType], all: false, ids },
      };
    });
  };

  const setScopeAll = (scopeType, checked) => {
    setScopeState((prev) => ({
      ...prev,
      [scopeType]: { all: checked, ids: checked ? [] : prev[scopeType].ids },
    }));
  };

  const toggleMember = (memberId) => {
    const member = String(memberId);
    setSelectedMembers((prev) =>
      prev.includes(member) ? prev.filter((item) => item !== member) : [...prev, member]
    );
  };

  const rowsForScope = (scopeType, options) => {
    const state = scopeState[scopeType];
    if (state.all) {
      if (hasWildcard(scopeType)) {
        return [{ scope_type: scopeType, scope_id: null }];
      }
      return options.map((option) => ({ scope_type: scopeType, scope_id: Number(option.id) }));
    }

    return state.ids.map((scopeId) => ({ scope_type: scopeType, scope_id: Number(scopeId) }));
  };

  const scopesPayload = () => [
    ...rowsForScope("branch", scopeList("branch")),
    ...rowsForScope("sub_branch", filteredSubBranches),
    ...rowsForScope("shift", filteredShifts),
  ];

  const membersPayload = () =>
    selectedMembers.map((memberId) => ({
      member_type: currentMemberType,
      member_id: Number(memberId),
    }));

  const hydrateScopeState = (scopes = []) => {
    const next = {
      branch: { all: false, ids: [] },
      sub_branch: { all: false, ids: [] },
      shift: { all: false, ids: [] },
    };

    scopes.forEach((scope) => {
      if (!next[scope.scope_type]) return;
      if (scope.scope_id === null || scope.scope_id === undefined) {
        next[scope.scope_type].all = true;
        next[scope.scope_type].ids = [];
      } else {
        next[scope.scope_type].ids.push(String(scope.scope_id));
      }
    });

    return next;
  };

  const renderRows = (parent = 0, level = 0) =>
    (grouped[parent] || []).map((perm) => {
      const hasChildren = (grouped[perm.id] || []).length > 0;
      const isOpen = expanded.includes(perm.id);
      const checked = selected.includes(perm.id);
      const childIds = descendantIds(perm.id);
      const checkedChildCount = childIds.filter((childId) => selected.includes(childId)).length;
      const isPartial = !checked && checkedChildCount > 0;

      return (
        <React.Fragment key={perm.id}>
          <SoftBox pl={level * 3} py={0.75} display="flex" alignItems="center" sx={{ userSelect: "none" }}>
            {hasChildren ? (
              <IconButton size="small" onClick={() => toggleExpand(perm.id)}>
                <ChevronRightIcon
                  fontSize="small"
                  sx={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "0.2s" }}
                />
              </IconButton>
            ) : (
              <Box width={40} />
            )}

            <input
              type="checkbox"
              checked={checked}
              onChange={() => togglePermission(perm.id)}
              ref={(input) => {
                if (input) input.indeterminate = isPartial;
              }}
              style={{ marginRight: 8 }}
            />

            <SoftTypography variant="body2" fontWeight={level === 0 ? 600 : 400}>
              {perm.name}
            </SoftTypography>
          </SoftBox>

          {hasChildren && (
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              {renderRows(perm.id, level + 1)}
            </Collapse>
          )}
        </React.Fragment>
      );
    });

  const renderScopePicker = (scopeType, title, options) => (
    <SoftBox mb={3}>
      <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
        <input
          type="checkbox"
          checked={scopeState[scopeType].all}
          onChange={(event) => setScopeAll(scopeType, event.target.checked)}
        />
        <SoftTypography variant="button" fontWeight="bold">
          All {title}
        </SoftTypography>
      </SoftBox>

      {!scopeState[scopeType].all && (
        <SoftBox display="flex" flexWrap="wrap" gap={1}>
          {options.length ? (
            options.map((option) => (
              <label key={`${scopeType}-${option.id}`} style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 220 }}>
                <input
                  type="checkbox"
                  checked={scopeState[scopeType].ids.includes(String(option.id))}
                  onChange={() => toggleScopeId(scopeType, option.id)}
                />
                <span style={{ fontSize: 13 }}>{option.name}</span>
              </label>
            ))
          ) : (
            <SoftTypography variant="caption" color="text">
              No {title.toLowerCase()} available.
            </SoftTypography>
          )}
        </SoftBox>
      )}
    </SoftBox>
  );

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const controller = new AbortController();

    setLoading(true);

    (async () => {
      try {
        const [roleRes, permRes, scopeRes, rolesRes] = await Promise.all([
          fetch(`${API_URL}/access-roles/${id}`, {
            headers: authHeaders(token),
            signal: controller.signal,
          }),
          canAssignPermissions
            ? fetch(`${API_URL}/access-permissions?assignable=1`, {
                headers: authHeaders(token),
                signal: controller.signal,
              })
            : Promise.resolve({ ok: true, json: async () => ({ data: [] }) }),
          fetch(`${API_URL}/access-scope-options`, {
            headers: authHeaders(token),
            signal: controller.signal,
          }),
          canViewRoles
            ? fetch(`${API_URL}/access-roles?per_page=500&parent_options=1`, {
                headers: authHeaders(token),
                signal: controller.signal,
              })
            : Promise.resolve({ ok: true, json: async () => ({ roles: { data: [] } }) }),
        ]);

        if (!roleRes.ok || !permRes.ok || !scopeRes.ok || !rolesRes.ok) throw new Error("API Error");

        const roleJson = await roleRes.json();
        const permJson = await permRes.json();
        const scopeJson = await scopeRes.json();
        const rolesJson = await rolesRes.json();
        const roleRows = rolesJson.roles?.data || rolesJson.roles || rolesJson.data || [];

        const loadedActorType = roleJson.role?.actor_type || "internal";
        const loadedMemberType = memberTypeForActor(loadedActorType);

        setRole(roleJson.role);
        setRoleName(roleJson.role?.name || "");
        setActorType(loadedActorType);
        setShowInProjectManagementDropdown(
          Boolean(roleJson.role?.show_in_project_management_dropdown)
        );
        setParentRoleId(
          roleJson.role?.parent_role_id || roleJson.role?.effective_parent_role_id
            ? String(roleJson.role.parent_role_id || roleJson.role.effective_parent_role_id)
            : ""
        );
        const assignablePermissions = permJson.data || [];
        const assignablePermissionIds = new Set(assignablePermissions.map((permission) => permission.id));
        setPermissions(assignablePermissions);
        setScopeOptions(scopeJson.data || {});
        setParentRoles(Array.isArray(roleRows) ? roleRows : []);
        setSelected(
          (roleJson.role?.permissions?.map((p) => p.id) || []).filter(
            (permissionId) => !canAssignPermissions || assignablePermissionIds.has(permissionId)
          )
        );
        setScopeState(hydrateScopeState(roleJson.role?.scopes || []));
        setUserScope(roleJson.role?.user_scope || "self");
        const loadedMemberIds =
          roleJson.role?.members
            ?.filter((member) => member.member_type === loadedMemberType)
            .map((member) => String(member.member_id)) || [];

        setSelectedMembers(loadedMemberIds);
        setInitialSelectedMembers(loadedMemberIds);
        setInitialMemberType(loadedMemberType);
      } catch (e) {
        setSnack({ open: true, color: "error", message: "Failed to load data" });
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [id, canAssignPermissions, canViewRoles]);

  const handleSave = async () => {
    const token = localStorage.getItem("authToken");
    setSaving(true);

    try {
      if (canUpdateRole) {
        if (!roleName.trim()) {
          throw new Error("Please enter role name");
        }

        const roleRes = await fetch(`${API_URL}/access-roles/${id}`, {
          method: "PUT",
          headers: authHeaders(token, { "Content-Type": "application/json" }),
          body: JSON.stringify({
            name: roleName.trim(),
            actor_type: actorType,
            parent_role_id: parentRoleId ? Number(parentRoleId) : null,
            user_scope: userScope,
            ...(canManageProjectManagementDropdownFlag
              ? {
                  show_in_project_management_dropdown:
                    actorType === "internal" && showInProjectManagementDropdown,
                }
              : {}),
          }),
        });
        const roleData = await roleRes.json().catch(() => ({}));
        if (!roleRes.ok) {
          throw new Error(roleData.message || Object.values(roleData.errors || {})?.[0]?.[0] || "Role details save failed");
        }
        setRole(roleData.role || role);
      }

      if (canAssignPermissions) {
        const res = await fetch(`${API_URL}/access-roles/${id}/permissions`, {
          method: "PUT",
          headers: authHeaders(token, { "Content-Type": "application/json" }),
          body: JSON.stringify({ permissions: selected }),
        });
        if (!res.ok) throw new Error("Permission save failed");
      }

      if (canAssignScopes) {
        const scopeRes = await fetch(`${API_URL}/access-roles/${id}/scopes`, {
          method: "PUT",
          headers: authHeaders(token, { "Content-Type": "application/json" }),
          body: JSON.stringify({ user_scope: userScope, scopes: scopesPayload() }),
        });
        if (!scopeRes.ok) throw new Error("Data access save failed");
      }

      const membersChanged =
        initialMemberType !== currentMemberType ||
        normalizedMemberIds(selectedMembers) !== normalizedMemberIds(initialSelectedMembers);

      if (canAssignMembers && membersChanged) {
        const memberRes = await fetch(`${API_URL}/access-roles/${id}/members`, {
          method: "PUT",
          headers: authHeaders(token, { "Content-Type": "application/json" }),
          body: JSON.stringify({ member_type: currentMemberType, members: membersPayload() }),
        });
        if (!memberRes.ok) throw new Error("Role assignment save failed");
      }

      setSnack({ open: true, color: "success", message: "Role access updated successfully" });
    } catch (e) {
      setSnack({ open: true, color: "error", message: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snack.color}
        icon="notifications"
        title="Role"
        content={snack.message}
        open={snack.open}
        close={() => setSnack((s) => ({ ...s, open: false }))}
        time={3000}
      />

      {loading ? (
        <SoftBox py={10} display="flex" justifyContent="center">
          <CircularProgress />
        </SoftBox>
      ) : (
        <SoftBox p={3}>
          <SoftTypography variant="h5" mb={3}>
            Access for <strong>{role?.name}</strong>
          </SoftTypography>

          <Card sx={{ p: 2, mb: 3 }}>
            <SoftTypography variant="h6" mb={2}>
              Role Details
            </SoftTypography>

            <SoftBox display="flex" flexWrap="wrap" gap={2}>
              <SoftBox sx={{ width: { xs: "100%", sm: 320 } }}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Role Name
                </SoftTypography>
                <SoftInput
                  placeholder="Role Name"
                  value={roleName}
                  size="small"
                  onChange={(event) => setRoleName(event.target.value)}
                  fullWidth
                  disabled={saving || !canUpdateRole}
                />
              </SoftBox>

              <SoftBox sx={{ width: { xs: "100%", sm: 220 } }}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Role Type
                </SoftTypography>
                <select
                  value={actorType}
                  onChange={(event) => {
                    setActorType(event.target.value);
                    setSelectedMembers([]);
                  }}
                  disabled={saving || !canUpdateRole}
                  style={{ width: "100%", padding: 8, border: "1px solid #d2d6da", borderRadius: 6 }}
                >
                  <option value="internal">Internal</option>
                  <option value="client">Client</option>
                  <option value="vendor">Vendor</option>
                  <option value="all">All</option>
                </select>
              </SoftBox>

              <SoftBox sx={{ width: { xs: "100%", sm: 260 } }}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Upper Role
                </SoftTypography>
                <select
                  value={parentRoleId}
                  onChange={(event) => setParentRoleId(event.target.value)}
                  disabled={saving || !canUpdateRole || !canViewRoles}
                  style={{ width: "100%", padding: 8, border: "1px solid #d2d6da", borderRadius: 6 }}
                >
                  <option value="">No Upper Role</option>
                  {parentRoleOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </SoftBox>

              {canManageProjectManagementDropdownFlag && (
                <SoftBox
                  sx={{
                    width: { xs: "100%", sm: 380 },
                    display: "flex",
                    alignItems: "flex-end",
                    pb: 0.5,
                  }}
                >
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={actorType === "internal" && showInProjectManagementDropdown}
                      disabled={saving || !canUpdateRole || actorType !== "internal"}
                      onChange={(event) => setShowInProjectManagementDropdown(event.target.checked)}
                    />
                    <span>Show this role in Project Management dropdown on bids page</span>
                  </label>
                </SoftBox>
              )}
            </SoftBox>
          </Card>

          {canAssignPermissions && (
            <Card sx={{ p: 2, mb: 3 }}>
              <SoftTypography variant="h6" mb={1}>
                Permissions
              </SoftTypography>
              {renderRows()}
            </Card>
          )}

          {canAssignScopes && (
            <Card sx={{ p: 2, mb: 3 }}>
              <SoftTypography variant="h6" mb={2}>
                Internal Data Access
              </SoftTypography>

              <SoftBox mb={3}>
                <SoftTypography variant="button" fontWeight="bold" display="block" mb={1}>
                  User Access
                </SoftTypography>
                <select
                  value={userScope}
                  onChange={(event) => setUserScope(event.target.value)}
                  style={{ width: 260, padding: 8, border: "1px solid #d2d6da", borderRadius: 6 }}
                >
                  <option value="self">Self</option>
                  <option value="all">All Users In Selected Scope</option>
                </select>
              </SoftBox>

              {renderScopePicker("branch", "Branches", scopeList("branch"))}
              {renderScopePicker("sub_branch", "Sub Branches", filteredSubBranches)}
              {renderScopePicker("shift", "Shifts", filteredShifts)}
            </Card>
          )}

          {canAssignMembers && (
            <Card sx={{ p: 2, mb: 3 }}>
              <SoftTypography variant="h6" mb={1}>
                Assign Role To {memberTitle}
              </SoftTypography>

              <SoftBox display="flex" flexWrap="wrap" gap={1}>
                {memberOptions.length ? (
                  memberOptions.map((option) => (
                    <label key={option.id} style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 220 }}>
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(String(option.id))}
                        onChange={() => toggleMember(option.id)}
                      />
                      <span style={{ fontSize: 13 }}>{option.name}</span>
                    </label>
                  ))
                ) : (
                  <SoftTypography variant="caption" color="text">
                    No {memberTitle.toLowerCase()} available in your access scope.
                  </SoftTypography>
                )}
              </SoftBox>
            </Card>
          )}

          <SoftBox mt={4} display="flex" gap={2}>
            <SoftButton variant="gradient" color="dark" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Update Role Access"}
            </SoftButton>

            <SoftButton variant="outlined" color="dark" onClick={() => navigate("/roles/list")}>
              Back
            </SoftButton>
          </SoftBox>
        </SoftBox>
      )}
    </DashboardLayout>
  );
};

export default RolePermissions;
